/**
 * Per-entity performance: campaigns, buyers, suppliers and destinations.
 *
 * One table component for all four. The legacy response names the same
 * measurement differently per entity — `delivery_rate` on campaigns,
 * `success_rate` on buyers — which forced the old UI to special-case each one.
 * The v1 endpoint normalises to `rate`, because it is the same number.
 */
import { Alert, Badge, Card, Group, Loader, Stack, Table, Tabs, Text } from '@mantine/core';
import { useEntityStats } from '../api/queries';
import type { components } from '../api/schema';
import { TOKENS, formatGBP, formatNumber } from '../theme';
import { useVocab } from '../vocab';

type Stat = components['schemas']['EntityStat'];
type Money = components['schemas']['MoneyVisibility'];

function StatTable({ rows, noun, money }: { rows: Stat[]; noun: string; money: Money }) {
  const v = useVocab();
  const ownCost = money.cost && !money.revenue;
  if (rows.length === 0) {
    return <Text c="dimmed" size="sm" py="lg" ta="center">No {noun} activity in this period.</Text>;
  }
  return (
    <Table.ScrollContainer minWidth={700}>
      <Table striped horizontalSpacing={6} verticalSpacing={7}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th ta="right">{v.Many('lead')}</Table.Th>
            <Table.Th ta="right">Delivered</Table.Th>
            <Table.Th ta="right">Failed</Table.Th>
            <Table.Th ta="right">Rate</Table.Th>
            {money.revenue && <Table.Th ta="right">Revenue</Table.Th>}
            {money.cost && <Table.Th ta="right">{ownCost ? 'Earned' : 'Cost'}</Table.Th>}
            {money.margin && <Table.Th ta="right">Margin</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr key={r.name}>
              <Table.Td><Text size="sm" truncate>{r.name}</Text></Table.Td>
              <Table.Td ta="right">{formatNumber(r.total_leads)}</Table.Td>
              <Table.Td ta="right">{formatNumber(r.delivered)}</Table.Td>
              <Table.Td ta="right">
                <Text size="sm" c={r.failed > 0 ? TOKENS.redText : undefined}>
                  {formatNumber(r.failed)}
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <Badge
                  size="sm"
                  variant="light"
                  color={r.rate >= 50 ? 'chartGreen' : r.rate > 0 ? 'gray' : 'chartRed'}
                >
                  {r.rate.toFixed(1)}%
                </Badge>
              </Table.Td>
              {money.revenue && <Table.Td ta="right">{formatGBP(r.total_revenue)}</Table.Td>}
              {money.cost && <Table.Td ta="right">{formatGBP(r.total_payout)}</Table.Td>}
              {money.margin && (
                <Table.Td ta="right">
                  <Text size="sm" c={(r.margin ?? 0) < 0 ? TOKENS.redText : undefined}>
                    {r.margin != null ? formatGBP(r.margin) : '—'}
                    {r.margin_pct != null && (
                      <Text span size="xs" c="dimmed">{` ${r.margin_pct.toFixed(1)}%`}</Text>
                    )}
                  </Text>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

export function ReportsEntities({ range }: { range: string }) {
  const v = useVocab();
  const { data, isPending, error } = useEntityStats({
    date_range: range,

  });

  if (error) return <Alert color="red" variant="light">{error.message}</Alert>;
  if (isPending) return <Group justify="center" p="xl"><Loader /></Group>;

  const groups = [
    { key: 'campaigns', label: v.Many('campaign'), noun: v.many('campaign'), rows: data?.campaigns ?? [] },
    { key: 'buyers', label: v.Many('buyer'), noun: v.many('buyer'), rows: data?.buyers ?? [] },
    { key: 'suppliers', label: v.Many('supplier'), noun: v.many('supplier'), rows: data?.suppliers ?? [] },
    { key: 'destinations', label: v.Many('destination'), noun: v.many('destination'), rows: data?.destinations ?? [] },
  ];

  return (
    <Stack>
      <Tabs defaultValue="campaigns" variant="pills">
        <Tabs.List mb="md">
          {groups.map((g) => (
            <Tabs.Tab key={g.key} value={g.key}>
              {g.label}
              {g.rows.length > 0 && (
                <Text span size="xs" c="dimmed"> ({g.rows.length})</Text>
              )}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {groups.map((g) => (
          <Tabs.Panel key={g.key} value={g.key}>
            <Card p={0}>
              <StatTable
                rows={g.rows}
                noun={g.noun}
                money={data?.money ?? { revenue: true, cost: false, margin: false }}
              />
            </Card>
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
}
